import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Popover } from "@mui/material";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../ui/button";
import "./AmsDateRangeCalendar.css";

// ── DateRange types ──────────────────────────────────────────────────────────
export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

type AmsDateRangeCalendarProps = {
  value: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  defaultMonth?: Date;
  className?: string;
  singleDate?: boolean;
};

// ── Calendar grid helpers ────────────────────────────────────────────────────
const DOW = ["S", "M", "T", "W", "T", "F", "S"] as const;

const addDays = (d: Date, n: number): Date => dayjs(d).add(n, "day").toDate();
const addMonths = (d: Date, n: number): Date => dayjs(d).add(n, "month").toDate();
const isSameDay = (a: Date, b: Date): boolean => dayjs(a).isSame(dayjs(b), "day");
const startOfDay = (d: Date): Date => dayjs(d).startOf("day").toDate();
const cmpDay = (a: Date, b: Date): number => {
  const sa = dayjs(a).startOf("day");
  const sb = dayjs(b).startOf("day");
  return sa.isBefore(sb) ? -1 : sa.isAfter(sb) ? 1 : 0;
};

function monthMatrix(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const start = addDays(first, -startDow);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return cells;
}

// ── AmsDateRangeCalendar (internal) ─────────────────────────────────────────
function AmsDateRangeCalendar({
  value,
  onSelect,
  defaultMonth,
  className,
  singleDate = false,
}: AmsDateRangeCalendarProps) {
  const [view, setView] = useState(() => {
    const seed = defaultMonth ?? value?.from ?? value?.to ?? new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });
  const [hover, setHover] = useState<Date | null>(null);

  const defaultMonthTime = defaultMonth?.getTime() ?? null;
  useEffect(() => {
    if (defaultMonthTime == null) return;
    const dm = new Date(defaultMonthTime);
    setView(new Date(dm.getFullYear(), dm.getMonth(), 1));
  }, [defaultMonthTime]);

  const today = useMemo(() => startOfDay(new Date()), []);

  const { rStart, rEnd } = useMemo(() => {
    const from = value?.from;
    const to = value?.to;
    if (!from) return { rStart: undefined as Date | undefined, rEnd: undefined as Date | undefined };

    const collapsed = to && isSameDay(from, to);
    const previewOpen = !to || collapsed;

    if (previewOpen && hover && !isSameDay(hover, from)) {
      if (cmpDay(hover, from) < 0) return { rStart: hover, rEnd: from };
      return { rStart: from, rEnd: hover };
    }
    if (to) return { rStart: from, rEnd: to };
    return { rStart: from, rEnd: from };
  }, [value?.from, value?.to, hover]);

  const pick = useCallback(
    (d: Date) => {
      if (singleDate) {
        onSelect({ from: d, to: undefined });
        return;
      }

      const from = value?.from;
      const to = value?.to;

      if (!from) {
        onSelect({ from: d, to: undefined });
        return;
      }
      if (from && !to) {
        if (cmpDay(d, from) < 0) onSelect({ from: d, to: from });
        else onSelect({ from, to: d });
        return;
      }
      if (from && to && !isSameDay(from, to)) {
        onSelect({ from: d, to: undefined });
        return;
      }
      if (isSameDay(d, from)) return;

      const earlier = cmpDay(d, from) < 0 ? d : from;
      const later = cmpDay(d, from) < 0 ? from : d;
      onSelect({ from: earlier, to: later });
    },
    [onSelect, value?.from, value?.to, singleDate],
  );

  const year = view.getFullYear();
  const month = view.getMonth();
  const cells = useMemo(() => monthMatrix(year, month), [year, month]);

  return (
    <div className={`ams-drp drp${className ? ` ${className}` : ""}`}>
      <div className="ams-drp__head">
        <button
          type="button"
          className="ams-drp__navbtn"
          aria-label="Previous month"
          onClick={() => setView((v) => addMonths(v, -1))}
        >
          <ChevronLeft className="size-[18px] shrink-0" aria-hidden />
        </button>
        <div className="ams-drp__title">{dayjs(view).format("MMMM YYYY")}</div>
        <button
          type="button"
          className="ams-drp__navbtn"
          aria-label="Next month"
          onClick={() => setView((v) => addMonths(v, 1))}
        >
          <ChevronRight className="size-[18px] shrink-0" aria-hidden />
        </button>
      </div>

      <div className="drp__dow">
        {DOW.map((label, i) => (
          <span key={i}>{label}</span>
        ))}
      </div>

      <div>
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="drp__week">
            {cells.slice(row * 7, row * 7 + 7).map((d, col) => {
              const inMonth = d.getMonth() === month;
              const isToday = isSameDay(d, today);
              const isStart = rStart ? isSameDay(d, rStart) : false;
              const isEnd = rEnd ? isSameDay(d, rEnd) : false;
              const isIn =
                rStart && rEnd ? cmpDay(d, rStart) > 0 && cmpDay(d, rEnd) < 0 : false;

              const cls = [
                "drp__cell",
                !inMonth && "out",
                isToday && "today",
                isStart && "start",
                isEnd && "end",
                isIn && "in",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  key={col}
                  type="button"
                  className={cls}
                  onClick={() => pick(d)}
                  onMouseEnter={() => setHover(d)}
                  onMouseLeave={() => setHover(null)}
                >
                  <span className="num">{d.getDate()}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface IDateRangeCalendarPickerProps {
  fromValue: number | Dayjs | null | undefined;
  toValue?: number | Dayjs | null | undefined;
  onFromChange: (dateTime: Dayjs | null) => void;
  onToChange?: (dateTime: Dayjs | null) => void;
  triggerClassName?: string;
  hideTrigger?: boolean;
  open?: boolean;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
  /** When true, acts as a single-date picker. */
  singleDate?: boolean;
  /** Disables the trigger button and prevents the popover from opening. */
  disabled?: boolean;
}

// ── helpers ──────────────────────────────────────────────────────────────────
function toDate(v: number | Dayjs | null | undefined): Date | undefined {
  if (!v) return undefined;
  return dayjs(v).toDate();
}

function setDatePart(base: Date, dateStr: string): Date | null {
  // expects "MM/DD/YYYY"
  const [mm, dd, yyyy] = dateStr.split("/").map(Number);
  if (!mm || !dd || !yyyy || yyyy < 1900) return null;
  const d = new Date(base);
  d.setFullYear(yyyy, mm - 1, dd);
  return isNaN(d.getTime()) ? null : d;
}

function setTimePart(base: Date, timeStr: string): Date {
  // expects "HH:mm" or "HH:mm:ss"
  const seg = timeStr.split(":");
  const hh = parseInt(seg[0] ?? "", 10);
  const mm = parseInt(seg[1] ?? "", 10);
  const ss = seg.length > 2 ? parseInt(seg[2] ?? "", 10) : 0;
  if (isNaN(hh) || isNaN(mm) || isNaN(ss)) return base;
  const d = new Date(base);
  d.setHours(hh, mm, ss, 0);
  return d;
}

function formatDateInput(d: Date | undefined): string {
  if (!d) return "";
  return dayjs(d).format("MM/DD/YYYY");
}

function formatTimeInput(d: Date | undefined): string {
  if (!d) return "";
  return dayjs(d).format("HH:mm:ss");
}

function formatTrigger(from: Date | undefined, to: Date | undefined): string {
  if (from && to) {
    return `${dayjs(from).format("MMM D, YYYY")} – ${dayjs(to).format("MMM D, YYYY")}`;
  }
  if (from) return `From ${dayjs(from).format("MMM D, YYYY")}`;
  return "Custom";
}

// ── time-input CSS shared class ───────────────────────────────────────────────
const TIME_INPUT_CLS =
  "h-9 flex-1 min-w-0 border border-input bg-background px-1.5 text-sm font-normal text-foreground tabular-nums shadow-sm rounded-md " +
  "focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring";

const TIME_VALUE_INPUT_CLS = `${TIME_INPUT_CLS} ams-time-input pr-1`;

// ── component ────────────────────────────────────────────────────────────────

const DateRangeCalendarPicker: React.FC<IDateRangeCalendarPickerProps> = ({
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  triggerClassName,
  hideTrigger = false,
  open,
  anchorEl,
  onClose,
  singleDate = false,
  disabled = false,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const appliedRef = useRef<DateRange | undefined>(undefined);
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<DateRange>({ from: undefined, to: undefined });
  const [startDateStr, setStartDateStr] = useState("");
  const [endDateStr, setEndDateStr] = useState("");

  const isControlled = typeof open === "boolean";
  const popoverAnchorEl = isControlled ? anchorEl ?? null : internalAnchorEl;
  const isOpen = isControlled ? !!open : internalOpen;

  // Click-outside handler for uncontrolled inline dropdown
  useEffect(() => {
    if (isControlled || !internalOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setInternalOpen(false);
        const applied = appliedRef.current;
        if (applied) setDraft({ from: applied.from, to: applied.to });
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isControlled, internalOpen]);

  // Sync applied values into ref so we can restore on popover close
  useEffect(() => {
    appliedRef.current = { from: toDate(fromValue), to: toDate(toValue) };
  }, [fromValue, toValue]);

  // Keep date text inputs in sync with draft
  useEffect(() => {
    setStartDateStr(formatDateInput(draft.from));
  }, [draft.from]);

  useEffect(() => {
    setEndDateStr(formatDateInput(draft.to));
  }, [draft.to]);

  // Auto-open on mount (mirrors Vite app: popover opens right after "Custom" is selected)
  // Skipped in singleDate mode (no auto-open needed) and when disabled.
  useEffect(() => {
    if (hideTrigger || isControlled || singleDate || disabled) return;
    const timer = requestAnimationFrame(() => {
      if (triggerRef.current) {
        const from = toDate(fromValue) ?? dayjs().startOf("day").toDate();
        const to = toDate(toValue) ?? dayjs().endOf("day").toDate();
        setDraft({ from, to });
        setInternalAnchorEl(triggerRef.current);
        setInternalOpen(true);
      }
    });
    return () => cancelAnimationFrame(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideTrigger, isControlled]);

  useEffect(() => {
    if (!isControlled || !open) return;
    const from = toDate(fromValue) ?? dayjs().startOf("day").toDate();
    const to = toDate(toValue) ?? dayjs().endOf("day").toDate();
    setDraft({ from, to });
  }, [isControlled, open, fromValue, toValue]);

  const openPopover = () => {
    if (hideTrigger || isControlled || disabled) return;
    const from = toDate(fromValue) ?? dayjs().startOf("day").toDate();
    const to = toDate(toValue) ?? dayjs().endOf("day").toDate();
    setDraft({ from, to });
    setInternalAnchorEl(triggerRef.current);
    setInternalOpen(true);
  };

  const closePopover = () => {
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
      setInternalAnchorEl(null);
    }
    // Restore to last applied if user dismisses without applying
    const applied = appliedRef.current;
    if (applied) {
      setDraft({ from: applied.from, to: applied.to });
    }
  };

  const handleCalendarSelect = useCallback((range: DateRange | undefined) => {
    if (singleDate) {
      if (!range?.from) return;
      const d = range.from;
      const selected = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
      appliedRef.current = { from: selected, to: selected };
      setDraft({ from: selected, to: selected });
      onFromChange(dayjs(selected));
      onToChange?.(dayjs(selected));
      if (isControlled) {
        onClose?.();
      } else {
        setInternalOpen(false);
        setInternalAnchorEl(null);
      }
      return;
    }

    if (!range?.from) {
      setDraft({ from: undefined, to: undefined });
      return;
    }
    setDraft((prev) => {
      const from = range.from!;
      // preserve the time from previous selection if same day logic not needed here
      const prevFrom = prev.from;
      const mergedFrom = prevFrom
        ? new Date(from.getFullYear(), from.getMonth(), from.getDate(),
          prevFrom.getHours(), prevFrom.getMinutes(), prevFrom.getSeconds())
        : new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0);

      if (!range.to) {
        const prevTo = prev.to;
        const mergedTo = prevTo
          ? new Date(from.getFullYear(), from.getMonth(), from.getDate(),
            prevTo.getHours(), prevTo.getMinutes(), prevTo.getSeconds())
          : new Date(from.getFullYear(), from.getMonth(), from.getDate(), 23, 59, 59);
        return { from: mergedFrom, to: mergedTo };
      }

      const to = range.to;
      const prevTo = prev.to;
      const mergedTo = prevTo
        ? new Date(to.getFullYear(), to.getMonth(), to.getDate(),
          prevTo.getHours(), prevTo.getMinutes(), prevTo.getSeconds())
        : new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59);
      return { from: mergedFrom, to: mergedTo };
    });
  }, [singleDate, isControlled, onClose, onFromChange, onToChange]);

  const commitStartDate = () => {
    const d = setDatePart(draft.from ?? new Date(), startDateStr);
    if (!d) {
      setStartDateStr(formatDateInput(draft.from));
      return;
    }
    setDraft((prev) => {
      const from = new Date(d.getFullYear(), d.getMonth(), d.getDate(),
        prev.from?.getHours() ?? 0, prev.from?.getMinutes() ?? 0, prev.from?.getSeconds() ?? 0);
      let to = prev.to;
      if (to && from > to) {
        to = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 23, 59, 59);
      }
      return { from, to };
    });
  };

  const commitEndDate = () => {
    const d = setDatePart(draft.to ?? new Date(), endDateStr);
    if (!d || !draft.from) {
      setEndDateStr(formatDateInput(draft.to));
      return;
    }
    setDraft((prev) => {
      const to = new Date(d.getFullYear(), d.getMonth(), d.getDate(),
        prev.to?.getHours() ?? 23, prev.to?.getMinutes() ?? 59, prev.to?.getSeconds() ?? 59);
      return { ...prev, to };
    });
  };

  const handleApply = () => {
    if (!draft.from || !draft.to) return;
    appliedRef.current = { from: draft.from, to: draft.to };
    onFromChange(dayjs(draft.from));
    onToChange?.(dayjs(draft.to));
    if (isControlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
      setInternalAnchorEl(null);
    }
  };

  const triggerLabel = singleDate
    ? (toDate(fromValue) ? dayjs(toDate(fromValue)).format("MMM D, YYYY") : "Select date")
    : formatTrigger(toDate(fromValue), toDate(toValue));

  const canApply =
    !!draft.from &&
    !!draft.to &&
    draft.from.getTime() <= draft.to.getTime();

  // Shared dropdown content used by both the inline div and the MUI Popover
  const dropdownContent = (
    <>
      {/* ── Calendar grid ── */}
      <div style={{ backgroundColor: "var(--popover)", paddingBottom: 4, paddingTop: 8 }}>
        <AmsDateRangeCalendar
          className="mx-auto rounded-lg"
          defaultMonth={draft?.from ?? draft?.to ?? new Date()}
          value={draft}
          onSelect={handleCalendarSelect}
          singleDate={singleDate}
        />
      </div>

      {/* ── Date + time inputs ── */}
      <div
        style={{
          borderTop: "1px solid var(--border)",
          backgroundColor: "var(--popover)",
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {/* Start row */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label className="text-xs font-medium text-muted-foreground">{singleDate ? "Date" : "Start"}</label>
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <input
              type="text"
              placeholder="MM/DD/YYYY"
              autoComplete="off"
              aria-label="Start date"
              value={startDateStr}
              onChange={(e) => setStartDateStr(e.target.value)}
              onBlur={commitStartDate}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              className={TIME_INPUT_CLS}
              style={{ width: singleDate ? "100%" : 110 }}
            />
            {!singleDate && (
              <input
                type="time"
                step={1}
                disabled={!draft.from}
                aria-label="Start time"
                value={formatTimeInput(draft.from)}
                onChange={(e) => {
                  const t = e.target.value;
                  if (!t) return;
                  setDraft((prev) => prev.from
                    ? { ...prev, from: setTimePart(prev.from, t) }
                    : prev);
                }}
                className={TIME_VALUE_INPUT_CLS}
                style={{ width: 110 }}
              />
            )}
          </div>
        </div>

        {/* End row — hidden in single-date mode */}
        {!singleDate && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label className="text-xs font-medium text-muted-foreground">End</label>
            <div style={{ display: "flex", gap: 6, width: "100%" }}>
              <input
                type="text"
                placeholder="MM/DD/YYYY"
                autoComplete="off"
                aria-label="End date"
                disabled={!draft.from}
                value={endDateStr}
                onChange={(e) => setEndDateStr(e.target.value)}
                onBlur={commitEndDate}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                className={TIME_INPUT_CLS}
                style={{ width: 110 }}
              />
              <input
                type="time"
                step={1}
                disabled={!draft.to}
                aria-label="End time"
                value={formatTimeInput(draft.to)}
                onChange={(e) => {
                  const t = e.target.value;
                  if (!t) return;
                  setDraft((prev) => prev.to
                    ? { ...prev, to: setTimePart(prev.to, t) }
                    : prev);
                }}
                className={TIME_VALUE_INPUT_CLS}
                style={{ width: 110 }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Apply button — hidden in single-date mode (selection auto-applies) ── */}
      {!singleDate && (
        <div
          style={{
            borderTop: "1px solid var(--border)",
            backgroundColor: "var(--popover)",
            padding: "10px 12px",
          }}
        >
          <Button
            type="button"
            className="w-full max-w-none"
            disabled={!canApply}
            onClick={handleApply}
          >
            Apply
          </Button>
        </div>
      )}

      {/* ── Timezone ── hidden in singleDate mode */}
      {!singleDate && (
        <div
          style={{
            borderTop: "1px solid var(--border)",
            backgroundColor: "color-mix(in oklch, var(--muted) 20%, transparent)",
            padding: "6px 12px",
            fontSize: 11,
            color: "var(--muted-foreground)",
          }}
        >
          {typeof Intl !== "undefined"
            ? `Local (${Intl.DateTimeFormat().resolvedOptions().timeZone})`
            : "Local"}
        </div>
      )}
    </>
  );

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block", width: triggerClassName?.includes("w-full") ? "100%" : undefined }}>
      {!hideTrigger && (
        <button
          ref={triggerRef}
          type="button"
          onClick={openPopover}
          disabled={disabled}
          className={`inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-transparent px-3 text-sm font-normal text-foreground shadow-sm transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-accent"} ${triggerClassName || ""}`}
          aria-label={singleDate ? "Select date" : "Select custom date range"}
        >
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="max-w-[320px] truncate">{triggerLabel}</span>
        </button>
      )}

      {/* ── Uncontrolled: inline div (no portal — safe inside Radix modals/sheets) ── */}
      {!isControlled && isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 9999,
            borderRadius: "12px",
            border: "1px solid var(--border)",
            backgroundColor: "var(--popover)",
            color: "var(--popover-foreground)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
            overflow: "hidden",
            width: "max-content",
          }}
        >
          {dropdownContent}
        </div>
      )}

      {/* ── Controlled: MUI Popover anchored to external element (GeneralFilter, AuditTrailDetails) ── */}
      {isControlled && (
        <Popover
          open={isOpen}
          anchorEl={popoverAnchorEl}
          onClose={closePopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: "6px",
              borderRadius: "12px",
              border: "1px solid var(--border)",
              backgroundColor: "var(--popover)",
              color: "var(--popover-foreground)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
              overflow: "hidden",
              minWidth: 0,
              width: "max-content",
            },
          }}
        >
          {dropdownContent}
        </Popover>
      )}
    </div>
  );
};

export default DateRangeCalendarPicker;

